import { ApplicationElement, html } from '@effectivastudio/lit-components';
import CommonActions from '@effectivastudio/lit-components/components/common_actions/element.js';

import { MutationController } from '@lit-labs/observers/mutation-controller.js';
import { classMap } from 'lit/directives/class-map.js';
import { when } from 'lit/directives/when.js';

import '../card_header/element.js';
import '../card_prefooter/element.js';
import '../date_widget/element.js';
import '../slideshow/element.js';

import '@effectivastudio/lit-components/components/collage/element.js';
import '@effectivastudio/lit-components/components/grid_list/element.js';
import '@effectivastudio/lit-components/components/label/element.js';
import '@effectivastudio/lit-components/components/list_item_10/element.js';
import '@effectivastudio/lit-components/components/icon/element.js';
import '@effectivastudio/lit-components/components/img_placeholder/element.js';
import '@effectivastudio/lit-components/components/time_range/element.js';
import '@effectivastudio/lit-components/components/avatar_list/element.js';
import '@effectivastudio/lit-components/components/social_share/element.js';

import translations from './translations.js';
import style from './style.lit.css';

/**
 * Card element that consists of a header, body and footer.
 *
 * NOTES:
 *
 * - All cards are viewed as other users, unless named "Owned by me".
 * - All card types have the same style in List and Full views, except the Event and Marketplace card.
 * - Cards that don’t have the same List and Full view, have an List view example.
 * - List view has no Origin component.
 * - Description text in List view is truncated with Rails and limited by character number. At the end it has ellipsis (...) and "More" link, which leads to Card Full view
 * - Event card has two different List views. The one shown in Agenda uses xs-card-v2.
 * - Banner is visible on all cards and only in Full view.
 * - News and Discussion cards look the same when Announcement, the rest of the cards have a specific Announcement example
 *
 * @prop uid - Id of the card
 * @prop shareable - If true shows share button
 * @prop channelId - Channel id for Pusher
 * @prop cardType - Card type icon (options: event, date, discussion, engagement, marketplace, news, organize, poll)
 * @prop dateStart - Used in Event card as event start time and event details (optional)
 * @prop dateEnd - Used in Event card as event end time and event details (optional)
 * @prop attendingState - Used in Event card for attending status (options: initial, going, cannot)
 * @prop isAnnouncement - Used to change card theme and Header look (optional)
 * @prop isFullView - Automatically set if card is in full view
 * @prop kind - Used to add label to the card, used in Marketplace and Engagement cards (options: offering, looking) (optional)
 * @prop lastPersonLike - Name of the last person that liked (only in Desktop version)
 * @prop gallery - Internal property => If present, indicates existence of collage; used for label position calculation
 * @prop slider - Internal property => If present, indicates existence of slider; used for label position calculation
 * @prop attachmentCount - Internal property => Number of attachments passed via "attachments" slot
 * @prop likeCount - Number of counts
 * @prop likeActive - Like status - if current_user liked the card
 * @prop commentsOpen - Used for toggling comments show
 * @prop commentsOn - Are comments enabled on this card
 * @prop commentCount - Number of comments
 * @prop acceptedInvitationsCount - Number of accepted invitations
 * @prop declinedInvitationsCount - Number of declined invitations
 * @prop totalInvitationSlots - Maximum number of accepted invitations
 * @prop participantAvatars - Users we want to show as participants. (array of objects with name and picture properties)
 * @prop participantsUrl - URL of a page with the list of all participants
 * @prop ownerId - Card owner id
 * @prop ctaValue - Call to action status (optional)
 * @prop contactOn - If true, contact button my be shown if other conditions are met
 *
 * @prop class - Only to evoke re-rendering when manipulating classes
 *
 * @slot header - For header content
 * @slot notice - For card notice
 * @slot top - For gallery, slider and similar content; when empty, renders placeholder
 * @slot title - For title content
 * @slot body - For body content
 * @slot options - For any component extending ef-card-option-wrapper.
 * @slot card-details - For Event and Marketplace card details
 * @slot footer-main - For footer icons
 * @slot footer-right - For footer dot menu
 * @slot comments - For card comments
 * @slot attachments - For card attachments
 * @slot options - For any component extending ef-card-option-wrapper.
 * @slot social-share - For ef-social-share.
 *
 * @cssprop [--card-padding=16px] Element padding
 * @cssprop [--card-background: var(--contrast-light)] Element background
 * @cssprop [--card-font-color: var(--dark-text)] Element font color
 * @cssprop [--card-font-color-secondary: var(--dark-text-secondary)] Body text font color
 */

export default class Card extends ApplicationElement {
  static styles = [ApplicationElement.styles, style];

  static properties = {
    uid: { type: Number },
    shareable: { type: Boolean },
    channelId: { type: String },
    cardType: { type: String },
    dateStart: { type: Date },
    dateEnd: { type: Date },
    attendingState: { type: String },
    isAnnouncement: { type: Boolean, reflect: true },
    isFullView: { type: Boolean },
    kind: { type: String },
    lastPersonLike: { type: String },
    likeCount: { type: Number },
    likeActive: { type: Boolean },
    commentsOpen: { type: Boolean },
    commentsOn: { type: Boolean },
    commentCount: { type: Number },
    attachmentCount: { type: Number, state: true },
    acceptedInvitationsCount: { type: Number },
    totalInvitationSlots: { type: Number },
    declinedInvitationsCount: { type: Number },
    participantAvatars: { type: Array },
    participantsUrl: { type: String },
    ctaValue: { type: Boolean },
    ownerId: { type: Number },
    gallery: { state: true },
    slider: { state: true },
    contactOn: { type: Boolean },
    class: { type: String },
  };

  /** @internal */
  static translations = translations;

  constructor() {
    super();
    this.commentsOn = true;
    this.commonActions = new CommonActions(this);
    this.isFullView = /.+_cards\/\d+/.test(window.location.href);
    this.attendingState = null;
    this.acceptedInvitationsCount = 0;
    this.participantAvatars = [];
  }

  willUpdate(changedProperties) {
    super.willUpdate(changedProperties);

    if (changedProperties.has('attendingState')) {
      this.participantAvatars = this.participantAvatars?.filter(
        a => a.id !== this.currentUser?.id
      );

      if (this.attendingState === this.eventStatuses.attend) {
        this.participantAvatars.push({
          id: this.currentUser?.id,
          name: this.currentUser?.fullName,
          picture: this.currentUser?.avatar,
        });

        this.dispatchEvent(
          new Event('xs-card-attend', { bubbles: true, composed: true })
        );
      }
    }
  }

  get poll_option_wrapper() {
    return this.querySelector('ef-poll-option-wrapper');
  }

  get organize_option_wrapper() {
    return this.querySelector('ef-organize-option-wrapper');
  }

  get date_option_wrapper() {
    return this.querySelector('ef-date-option-wrapper');
  }

  connectedCallback() {
    super.connectedCallback();
    this.subscribeToChannel();
    this.setupEvents();

    new MutationController(this, {
      target: this.slotTop,
      config: { childList: true, subtree: false },
      callback: mutations => this.mutationHandler(mutations),
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.pusher?.unsubscribe(this.channelId);
  }

  setupEvents() {
    this.addEventListener('ajax:success', this.handleEvent);
  }

  handleEvent(_e) {
    // console.log(e);
  }

  subscribeToChannel() {
    if (!this.pusher) return;
    if (this.pusherChannel) return;

    this.pusherChannel ||= this.pusher.subscribe(this.channelId);
    this.setupPusherEvents(this.pusherChannel);

    // Temporary hack so we can connect existing functionality in platform
    this.dispatchEvent(
      new CustomEvent('card-pusher-subscribed', {
        bubbles: true,
        composed: true,
        detail: { channel: this.pusherChannel },
      })
    );
  }

  // Prepare architecture for pusher events
  setupPusherEvents(channel) {
    channel.bind('card:update', this.onPusherCardUpdate.bind(this));
    // channel.bind('comment:update', this.onPusherCommentUpdate.bind(this));
    channel.bind('card:poll_update', this.onPusherCardPollUpdate.bind(this));
    channel.bind(
      'card:organize_update',
      this.onPusherCardOrganizeUpdate.bind(this)
    );
    channel.bind('card:date_update', this.onPusherCardDateUpdate.bind(this));

    // Comment
    channel.bind('comment:create', this.onPusherCommentCreate.bind(this));
    channel.bind('comment:update', this.onPusherCommentUpdate.bind(this));
  }

  onPusherCardUpdate(data) {
    Object.assign(this, data);
  }

  onPusherCommentCreate({ parentId, html }) {
    let position;
    let root;

    if (parentId) {
      const comment = this.querySelector(`xs-comment[commentId='${parentId}']`);
      root = comment.querySelector(`.js-comments-list`);
      position = 'beforeend';
      comment.repliesCount++;
    } else {
      this.commentCount++;
      root = this.querySelector('.js-comments-list');
      position = 'afterbegin';
    }

    root.insertAdjacentHTML(position, html);
  }

  onPusherCardPollUpdate(data) {
    // Because we can be subscribed but element is not yet loaded.
    if (!this.poll_option_wrapper) return;

    this.poll_option_wrapper.totalCount = data.totalCount;
    data.options.forEach(option => {
      const optionElement = this.querySelector(
        `ef-poll-option[itemId="${option.itemId}"]`
      );

      Object.assign(optionElement, option);
    });

    this.poll_option_wrapper.setTotalCounts();
  }

  onPusherCardOrganizeUpdate(data) {
    // Because we can be subscribed but element is not yet loaded.
    if (!this.organize_option_wrapper) return;

    const item = this.querySelector(
      `ef-organize-option[itemId="${data.item.itemId}"]`
    );

    const { userId, amountContributed } = data.initiatorData;
    // custom amount added by me
    if (amountContributed && userId === this.AppConfig.user?.id)
      item.check(amountContributed);

    Object.assign(item, data.item);
    this.organize_option_wrapper.totalCount = data.totalCount;
  }

  onPusherCardDateUpdate(data) {
    // Because we can be subscribed but element is not yet loaded.
    if (!this.date_option_wrapper) return;

    this.date_option_wrapper.totalCount = data.totalCount;
    data.options.forEach(option => {
      const optionElement = this.querySelector(
        `ef-date-option[itemId="${option.itemId}"]`
      );

      Object.assign(optionElement, option);
    });
  }

  // ONLY PROTOTYPE - CHECK AND CHANGE WHEN IMPLEMENTING
  onPusherCommentUpdate({ id, html, card_comment_likes_count }) {
    const comment = this.querySelector(`xs-comment[commentId='${id}']`);

    if (html) {
      const tmp = document.createElement('div');
      tmp.innerHTML = html;

      const commentBody = comment.querySelector('.body');
      commentBody.innerHTML = tmp.querySelector('.body').innerHTML;
    }

    if (card_comment_likes_count !== undefined) {
      comment.likeCount = card_comment_likes_count;
    }
  }

  /** @internal */
  get isFullEvent() {
    return (
      this.totalInvitationSlots &&
      this.acceptedInvitationsCount >= this.totalInvitationSlots
    );
  }

  /** @internal */
  get participantsLabelType() {
    const type = this.declinedInvitationsCount ? 'long' : 'short';
    return this.totalInvitationSlots ? `${type}_with_limit` : type;
  }

  /** @internal */
  get isActive() {
    const dateToday = new Date();

    return this.dateStart < dateToday && dateToday < this.dateEnd;
  }

  /** @internal */
  get isSmartCollapsed() {
    return this.classList.contains('card-smart-collapsed');
  }

  /** @internal */
  get pusher() {
    return window.pusher;
  }

  /** @internal */
  get slotTop() {
    return this.querySelector("[slot='top']");
  }

  /** @internal */
  get placeholder() {
    return this.shadowRoot.querySelector('ef-img-placeholder');
  }

  /** @internal */
  get shouldRenderPlaceholder() {
    return (
      !this.gallery &&
      !this.slider &&
      (this.cardType === 'event' ||
        this.cardType === 'marketplace' ||
        this.cardType === 'engagement')
    );
  }

  /** @internal */
  get commentsContent() {
    return this.shadowRoot.querySelector('.comments');
  }

  /** @internal */
  get iconLike() {
    return this.likeActive ? 'thumbs-up:solid' : 'thumbs-up';
  }

  /** @internal */
  get iconComment() {
    return this.commentCount && this.commentsOpen ? 'comment:solid' : 'comment';
  }

  /** @internal */
  get currentUser() {
    return this.AppConfig?.user;
  }

  /** @internal */
  get isCardOwner() {
    return this.currentUser?.id === this.ownerId;
  }

  /** @internal */
  get contactOwnerUrl() {
    return `/direct_messages/new?other_user_id=${this.ownerId}`;
  }

  /** @internal */
  get toggleCtaUrl() {
    const action =
      {
        marketplace: 'toggle_available',
      }[this.cardType] || 'toggle_active';

    return `/${this.cardType}_cards/${this.uid}/${action}`;
  }

  /** @internal */
  get toggleLikeUrl() {
    return `/cards/${this.uid}/toggle_like`;
  }

  /** @internal */
  eventStatuses = {
    attend: 'going',
    cannot: 'cannot',
  };

  /** @internal */
  get attendUrl() {
    return `/event_cards/${this.uid}/attend`;
  }

  /** @internal */
  get canNotAttendUrl() {
    return `/event_cards/${this.uid}/cannot`;
  }

  /** @internal */
  get withdrawUrl() {
    return `/event_cards/${this.uid}/leave`;
  }

  // TODO: We need to change this attendOn, canNotAttendOn, withdrawOn and UI
  /** @internal */
  get attendOn() {
    return (
      !this.isFullEvent && this.attendingState !== this.eventStatuses.attend
    );
  }

  /** @internal */
  get canNotAttendOn() {
    return this.attendOn && this.attendingState !== this.eventStatuses.cannot;
  }

  /** @internal */
  get withdrawOn() {
    return this.attendingState === this.eventStatuses.attend;
  }

  /** @internal */
  get showContactButton() {
    return this.contactOn && !this.isCardOwner && this.ctaValue;
  }

  /** @internal */
  get eventCardActions() {
    return [
      this.attendOn && this.getEventActionConfig(this.eventStatuses.attend),
      this.canNotAttendOn &&
        this.getEventActionConfig(this.eventStatuses.cannot),
      this.withdrawOn && this.getEventActionConfig(),
    ].filter(Boolean);
  }

  /** @internal */
  get engagementLabel() {
    return `engagement.toggle_${!!this.ctaValue}`;
  }

  /** @internal */
  get marketplaceLabel() {
    return `marketplace.toggle_${!!this.ctaValue}`;
  }

  /** @internal */
  get shouldRenderCtaItem() {
    return (
      (this.cardType === 'engagement' ||
        (this.cardType === 'marketplace' && this.isFullView)) &&
      (this.showContactButton || this.isCardOwner)
    );
  }

  /** @internal */
  get shouldRenderDoneLabel() {
    return (
      !this.ctaValue &&
      (this.cardType === 'engagement' ||
        (this.cardType === 'marketplace' && this.isFullView)) &&
      !this.isCardOwner
    );
  }

  getEventActionConfig(status) {
    let config;

    switch (status) {
      case this.eventStatuses.attend:
        config = {
          msg: this.t('events.snack.attend'),
          label: this.t('events.action.attend'),
          icon: 'check:solid',
          url: this.attendUrl,
          className: 'xs-btn xs-btn-lg btn-primary',
        };
        break;

      case this.eventStatuses.cannot:
        config = {
          msg: this.t('events.snack.cannot'),
          label: this.t('events.action.cannot'),
          icon: 'minus:solid',
          url: this.canNotAttendUrl,
          className: 'xs-btn xs-btn-lg btn-outline-primary',
        };
        break;

      default:
        config = {
          msg: this.t('events.snack.withdraw'),
          label: this.t('events.action.withdraw'),
          icon: 'ban:solid',
          url: this.withdrawUrl,
          className: 'xs-btn xs-btn-lg btn-outline-primary',
        };
    }

    return config;
  }

  async onLikeClick() {
    if (!this.authorizeUser()) return;

    const lastPersonLike = this.lastPersonLike;

    this.likeActive = !this.likeActive;
    const delta = this.likeActive ? 1 : -1;
    this.likeCount += delta;
    if (delta > 0) {
      this.lastPersonLike = this.currentUser.fullName;
    }

    try {
      await this.commonActions.patch(this.toggleLikeUrl);
    } catch {
      this.likeActive = !this.likeActive;
      this.likeCount += this.likeActive ? 1 : -1;
      this.lastPersonLike = lastPersonLike;
    }
  }

  onCommentClick() {
    this.commentsOpen = !this.commentsOpen;
  }

  onAttachmentClick() {
    const attachmentHeader = this.shadowRoot.querySelector(
      '.attachment-list > h3'
    );

    attachmentHeader.scrollIntoView({ behavior: 'smooth' });

    attachmentHeader.classList.add('scrolled-to');
    setTimeout(() => {
      attachmentHeader.classList.remove('scrolled-to');
    }, 1000);
  }

  mutationHandler() {
    this.gallery = this.querySelector('ef-collage');
    this.slider = this.querySelector('xs-slideshow');
  }

  authorizeUser() {
    if (this.currentUser?.id) return true;
    this.commonActions.visit('/login');
  }

  async handleCtaToggle() {
    try {
      this.ctaValue = !this.ctaValue; // Optimistic update +  before pusher event
      await this.commonActions.patch(this.toggleCtaUrl);
    } catch {
      this.ctaValue = !this.ctaValue; // Revert optimistic update in case of error
      this.commonActions.displaySnackBarError(this.t('snack.generic_error'));
    }
  }

  async handleEventAttendAction(url, msg) {
    if (!this.authorizeUser()) return;

    try {
      const res = await this.commonActions.put(url);
      const resJSON = await res.json();
      this.acceptedInvitationsCount = resJSON.acceptedInvitationsCount;
      this.declinedInvitationsCount = resJSON.declinedInvitationsCount;
      this.attendingState = resJSON.state;

      this.commonActions.displaySnackBarSuccess(msg);
    } catch {
      this.commonActions.displaySnackBarError(this.t('snack.generic_error'));
    }
  }

  handleCtaContact() {
    if (this.authorizeUser())
      this.commonActions.execJSFromResponse(this.contactOwnerUrl);
  }

  handleAttachmentSlotChange(e) {
    this.attachmentCount = e.target.assignedElements({ flatten: true }).length;
  }

  renderPlaceholder() {
    return html`<ef-img-placeholder icon="image"></ef-img-placeholder>`;
  }

  renderDateWidget() {
    return html`
      <xs-card-date-widget
        date=${this.isActive ? new Date() : this.dateStart}
        layout="block">
      </xs-card-date-widget>
    `;
  }

  renderLabel() {
    return html`
      <ef-label layout="md"> ${this.t(`kind.${this.kind}`)} </ef-label>
    `;
  }

  renderDoneLabel() {
    return html`
      <ef-label layout="md" theme="muted"> ${this.t('done')} </ef-label>
    `;
  }

  renderCtaLabel() {
    return {
      marketplace: this.marketplaceLabel,
      engagement: this.engagementLabel,
    }[this.cardType];
  }

  renderEventItems() {
    return html`
      <h3 class="xs-title-3">${this.t('events.participation')}</h3>

      <ef-grid-list
        class="event-actions"
        col="1"
        colSM="1"
        colMD="2"
        gap="large">
        ${this.eventCardActions.map(
          itemConfig => html`
            <button
              type="button"
              class=${itemConfig.className}
              @click=${() =>
                this.handleEventAttendAction(itemConfig.url, itemConfig.msg)}>
              <ef-icon name=${itemConfig.icon}></ef-icon>
              <span>${itemConfig.label}</span>
            </button>
          `
        )}
      </ef-grid-list>
    `;
  }

  renderEventParticipants() {
    return html`
      <ef-avatar-list
        labelSuffixOn
        .url=${this.participantsUrl}
        .users=${this.participantAvatars}
        .totalCount=${this.acceptedInvitationsCount}>
      </ef-avatar-list>
    `;
  }

  renderEventParticipantsDetails() {
    return html`
      <ef-list-item-10 class="v1 participant-details">
        <ef-icon name="user-check" slot="left"></ef-icon>
        <h2 class="title" slot="main">${this.t('events.participants')}</h2>
        <div class="info" slot="main">
          ${this.isFullEvent ? this.renderEventFullLabel() : null}
          ${this.t(`events.details.${this.participantsLabelType}`, {
            attending: +this.acceptedInvitationsCount,
            not_attending: +this.declinedInvitationsCount,
            limit: +this.totalInvitationSlots,
          })}
        </div>
      </ef-list-item-10>
    `;
  }

  renderEventFullLabel() {
    return html`
      <ef-label slot="main" layout="sm" theme="warning">
        ${this.t(`events.full`)}
      </ef-label>
    `;
  }

  renderContactButton() {
    return html`
      <button
        @click=${this.handleCtaContact}
        type="button"
        class="xs-btn btn-primary">
        <ef-icon name="comment-alt-lines"></ef-icon>
        <span>${this.t('contact')}</span>
      </button>
    `;
  }

  renderCtaToggleButton() {
    const config = {
      icon: this.ctaValue ? 'check:solid' : 'refresh:solid',
      label: this.renderCtaLabel(),
      className: this.ctaValue ? 'btn-primary' : 'btn-secondary',
    };

    return html`
      <button
        @click=${this.handleCtaToggle}
        type="button"
        class="xs-btn ${config.className}">
        <ef-icon name=${config.icon}> </ef-icon>
        <span>${this.t(config.label)}</span>
      </button>
    `;
  }

  renderCtaItem() {
    return html`
      <div class="card-actions">
        ${this.isCardOwner
          ? this.renderCtaToggleButton()
          : this.renderContactButton()}
      </div>
    `;
  }

  renderPrefooter() {
    return html`
      <xs-card-prefooter
        .card=${this}
        commentCount=${this.commentsOn ? this.commentCount : 0}
        likeCount=${this.likeCount}
        lastPersonLike=${this.lastPersonLike}>
      </xs-card-prefooter>
    `;
  }

  renderFull() {
    const bodyClasses = {
      truncate: !this.isFullView,
    };

    const wrapperClasses = {
      slider: this.slider,
    };

    return html`
      <slot name="header"></slot>

      <div class="body">
        <slot name="notice"></slot>
        <div class="top-wrapper ${classMap(wrapperClasses)}">
          <slot name="top">
            ${this.shouldRenderPlaceholder ? this.renderPlaceholder() : null}
          </slot>
          ${this.cardType === 'event' ? this.renderDateWidget() : null}
          ${this.kind ? this.renderLabel() : null}
        </div>

        <slot name="title"></slot>

        <slot name="card-details"></slot>

        ${this.cardType === 'event'
          ? this.renderEventParticipantsDetails()
          : null}

        <slot name="body" class=${classMap(bodyClasses)}></slot>

        <slot name="options"></slot>

        <section class="attachment-list" ?hidden=${!this.attachmentCount}>
          <h3 class="xs-title-3">${this.t('attachments')}</h3>
          <slot
            name="attachments"
            @slotchange=${this.handleAttachmentSlotChange}>
          </slot>
        </section>

        ${this.cardType === 'event' &&
        this.isFullView &&
        this.eventCardActions.length
          ? this.renderEventItems()
          : null}
        ${this.cardType === 'event' &&
        this.acceptedInvitationsCount &&
        this.isFullView
          ? this.renderEventParticipants()
          : null}
        ${this.shouldRenderCtaItem ? this.renderCtaItem() : null}
        ${this.shouldRenderDoneLabel ? this.renderDoneLabel() : null}
      </div>

      ${this.renderPrefooter()}

      <footer>
        <ef-list-item-10>
          <slot name="footer-main" slot="main">
            <ef-grid-list col="0" gap="large">
              <button @click=${this.onLikeClick} aria-label="like">
                <ef-icon name=${this.iconLike}></ef-icon>
              </button>

              ${when(
                this.commentsOn,
                () => html`
                  <button @click=${this.onCommentClick} aria-label="comment">
                    <ef-icon name=${this.iconComment}></ef-icon>
                  </button>
                `
              )}
              ${when(
                this.attachmentCount,
                () => html`
                  <button
                    @click=${this.onAttachmentClick}
                    aria-label="upload file">
                    <ef-icon name="paperclip"> </ef-icon>
                    <span class="count">${this.attachmentCount}</span>
                  </button>
                `
              )}
              ${when(
                this.shareable,
                () => html` <slot name="social-share"> </slot> `
              )}
            </ef-grid-list>
          </slot>
          <slot name="footer-right" slot="right">
            <xs-btn-more></xs-btn-more>
          </slot>
        </ef-list-item-10>

        ${when(
          this.commentsOn,
          () => html`
            <details class="comments" ?open=${this.commentsOpen}>
              <summary></summary>
              <slot name="comments"></slot>
            </details>
          `
        )}
      </footer>
    `;
  }

  renderCollapsed() {
    return html`
      <slot name="header"></slot>
      <div class="body">
        <slot name="title"></slot>
      </div>
    `;
  }

  render() {
    return this.isSmartCollapsed ? this.renderCollapsed() : this.renderFull();
  }
}

Card.registerTagName('xs-card');

customElements.get('xs-card') || customElements.define('xs-card', Card);
